/**
 * AgroTech — Analysis Review  (Step 7 of the 9-step intelligence loop)
 * Human-in-the-Loop gate 2: Operations Manager reviews AI drone diagnosis,
 * confirms or overrides, and creates a digital seal release for Step 8 remediation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardCheck, ShieldCheck, GitMerge, MessageSquare,
  CheckCircle2, AlertTriangle, RefreshCw,
  ChevronDown, ChevronUp, Activity, ArrowRight, Layers,
  Lock, Check, Sparkles, Download
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useNavigate } from 'react-router-dom';
import api, { agrotechDroneApi, reportsApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MissionWithAnalysis {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  updated_at: string;
  created_at: string;
  metadata?: {
    ai_analysis_status?: string;
    step7_status?: string;
    step7_decision?: string;
    step7_override_reason?: string;
    step7_override_diagnosis?: string;
    step7_reviewed_at?: string;
    step7_verification?: any;
    diagnosis?: string;
    severity?: string;
    affected_area_ha?: number;
    confidence?: number;
    payload?: string;
    boundary_name?: string;
    drone_analysis?: {
      diagnosis: string;
      category: string;
      severity: string;
      affected_area_ha: number;
      confidence: number;
      ndvi_drone_mean: number;
      ndvi_drone_depressed: number;
      satellite_match: boolean;
      correlation_notes: string;
      recommended_actions: Array<{ action: string; priority: string; remediation_type: string }>;
      narrative: string;
      ai_analysed_at: string;
    };
    [key: string]: any;
  };
}

const OVERRIDE_REASONS = [
  'AI missed visible context not captured in imagery',
  'Known local condition / historical pest baseline not in AI model',
  'Weather event between flight and analysis changed situation',
  'Mechanical or ground equipment anomaly misclassified as crop stress',
  'Other (specify below)',
];

export const AnalysisReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [missions, setMissions]     = useState<MissionWithAnalysis[]>([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState<'pending' | 'reviewed' | 'all'>('pending');
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);

  // Review modal / active review state
  const [reviewingMission, setReviewingMission] = useState<MissionWithAnalysis | null>(null);
  const [decision, setDecision] = useState<'confirm' | 'override' | 'additional_survey' | 'no_action'>('confirm');
  const [overrideReason, setOverrideReason] = useState(OVERRIDE_REASONS[0]);
  const [overrideDiagnosis, setOverrideDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadMissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { limit: 100 } });
      const all: MissionWithAnalysis[] = res.data?.data?.missions ?? res.data?.missions ?? [];
      const surveyed = all.filter(
        (m) => (m.mission_type === 'drone_survey' || m.metadata?.step === 5 || m.metadata?.anomaly_id) &&
               m.metadata?.ai_analysis_status === 'complete'
      );
      setMissions(surveyed);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load reviewed missions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMissions();
  }, [loadMissions]);

  const handleSubmitReview = async () => {
    if (!reviewingMission) return;
    setSubmitting(true);
    setError(null);
    try {
      await agrotechDroneApi.verifyDroneFindings(reviewingMission.id, {
        decision,
        overrideReason: decision === 'override' ? overrideReason : undefined,
        overrideDiagnosis: decision === 'override' ? overrideDiagnosis : undefined,
        notes: notes.trim() || undefined,
      });

      setSuccess(`Human Verification sign-off recorded with Digital Seal for ${reviewingMission.name}`);
      setReviewingMission(null);
      await loadMissions();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to record verification');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = missions.filter(m => !m.metadata?.step7_status || m.metadata.step7_status === 'pending_review').length;
  const reviewedCount = missions.filter(m => m.metadata?.step7_status && m.metadata.step7_status !== 'pending_review').length;

  const filteredMissions = missions.filter(m => {
    const isPending = !m.metadata?.step7_status || m.metadata.step7_status === 'pending_review';
    if (filter === 'pending') return isPending;
    if (filter === 'reviewed') return !isPending;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-teal-600" /> Human Analysis Review (Gate 2)
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 7 — Validate drone AI findings, confirm/override diagnosis, and release digital seal to Step 8 Remediation
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadMissions} className="flex items-center gap-1.5">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Metric Summary ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 text-center border-l-4 border-amber-500">
          <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Awaiting Agronomist Sign-Off</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-green-500">
          <p className="text-2xl font-bold text-green-600">{reviewedCount}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Digitally Sealed & Released</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-blue-500">
          <p className="text-2xl font-bold text-blue-600">{missions.length}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Total Drone Surveys</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-purple-500">
          <p className="text-2xl font-bold text-purple-600">100%</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">SHA-256 Audit Admissibility</p>
        </Card>
      </div>

      {/* ── Filter Tabs ── */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setFilter('pending')}
          className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors ${
            filter === 'pending'
              ? 'border-teal-500 text-teal-600 dark:text-teal-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Awaiting Review ({pendingCount})
        </button>
        <button
          onClick={() => setFilter('reviewed')}
          className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors ${
            filter === 'reviewed'
              ? 'border-teal-500 text-teal-600 dark:text-teal-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Digitally Sealed ({reviewedCount})
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors ${
            filter === 'all'
              ? 'border-teal-500 text-teal-600 dark:text-teal-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          All Surveys ({missions.length})
        </button>
      </div>

      {/* ── Main List ── */}
      {loading ? (
        <div className="flex justify-center items-center h-48">
          <Spinner size="lg" />
        </div>
      ) : filteredMissions.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <ClipboardCheck className="mx-auto w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {filter === 'pending' ? 'No Surveys Awaiting Verification' : 'No Reviewed Surveys Found'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto mt-1 mb-4">
              {filter === 'pending'
                ? 'When drone telemetry analysis completes in Step 6, missions appear here for agronomist decision gate.'
                : 'Missions will appear here once an agronomist sign-off is applied.'}
            </p>
            <Button variant="outline" size="sm" onClick={() => navigate('/agrotech/flight-analysis')}>
              Go to Step 6 — Flight Analysis →
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredMissions.map((m) => {
            const analysis = m.metadata?.drone_analysis;
            const isConfirmed = m.metadata?.step7_status === 'confirmed';
            const isOverridden = m.metadata?.step7_status === 'overridden';
            const isPending = !m.metadata?.step7_status || m.metadata.step7_status === 'pending_review';

            return (
              <Card key={m.id} className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">{m.name}</h3>
                      <span className={`text-xs px-2.5 py-0.5 rounded font-semibold uppercase ${
                        isConfirmed ? 'bg-green-100 text-green-700 dark:bg-green-900/30' :
                        isOverridden ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30' :
                        'bg-amber-100 text-amber-700 dark:bg-amber-900/30'
                      }`}>
                        {isPending ? 'Pending Sign-Off' : m.metadata?.step7_status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Field: {m.metadata?.boundary_name || 'Estate Field'} · Anomaly: {m.metadata?.anomaly_type || 'Vegetation Decline'}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {isPending ? (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setReviewingMission(m);
                          setDecision('confirm');
                          setNotes('');
                        }}
                        className="flex items-center gap-1.5"
                      >
                        <ShieldCheck className="w-4 h-4" /> Review & Sign-Off
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => reportsApi.downloadCertifiedPdf('mission_summary', m.id)}
                          className="flex items-center gap-1"
                        >
                          <Download className="w-3.5 h-3.5" /> PDF Seal
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/agrotech/remediation')}
                          className="flex items-center gap-1.5 text-teal-600 border-teal-500"
                        >
                          Go to Step 8 Remediation <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Side-by-Side Satellite vs Drone Comparison Box */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl mb-3 text-xs">
                  <div className="border-r border-gray-200 dark:border-gray-700 pr-3">
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">
                      🛰️ Step 3 Satellite Detection
                    </span>
                    <p className="font-semibold text-gray-800 dark:text-gray-200">
                      {m.metadata?.anomaly_type || 'NDVI Depressed Area'} (Severity: {m.metadata?.severity || 'high'})
                    </p>
                    <p className="text-gray-500 mt-1">
                      Initial area estimate: {m.metadata?.affected_area_hectares || 2.4} ha
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-teal-600 dark:text-teal-400 tracking-wider block mb-1">
                      🚁 Step 6 Drone High-Res Diagnosis
                    </span>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {analysis?.diagnosis || m.metadata?.diagnosis || 'Fungal root stress confirmed'}
                    </p>
                    <p className="text-gray-500 mt-1">
                      Refined area: {analysis?.affected_area_ha || m.metadata?.affected_area_ha || 1.8} ha · NDVI min: {analysis?.ndvi_drone_depressed || 0.28}
                    </p>
                  </div>
                </div>

                {/* Digital Seal info if signed off */}
                {!isPending && (
                  <div className="flex items-center gap-2 p-2.5 bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800 rounded-lg text-xs text-teal-800 dark:text-teal-300">
                    <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>
                      Digitally Sealed on {new Date(m.metadata?.step7_reviewed_at || m.updated_at).toLocaleString()} ·
                      Decision: <strong>{m.metadata?.step7_decision?.toUpperCase()}</strong>
                    </span>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Agronomist Sign-Off Modal ── */}
      {reviewingMission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-teal-600" /> Agronomist Decision Sign-Off Gate
              </h3>
              <button
                onClick={() => setReviewingMission(null)}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <div className="p-3 bg-teal-50 dark:bg-teal-950/30 rounded-lg text-xs space-y-1 text-teal-900 dark:text-teal-200">
              <p className="font-semibold">Reviewing: {reviewingMission.name}</p>
              <p>AI Drone Diagnosis: {reviewingMission.metadata?.drone_analysis?.diagnosis || reviewingMission.metadata?.diagnosis}</p>
              <p>Confidence: {Math.round((reviewingMission.metadata?.drone_analysis?.confidence || 0.9) * 100)}%</p>
            </div>

            {/* Decision selector */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase text-gray-500">Sign-Off Decision</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'confirm', label: '✅ Confirm & Release to Step 8' },
                  { id: 'override', label: '✍️ Override AI Diagnosis' },
                  { id: 'additional_survey', label: '🔄 Request Re-Survey (Step 5)' },
                  { id: 'no_action', label: '🛑 Close / No Action' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDecision(opt.id as any)}
                    className={`p-2.5 rounded-lg border text-xs font-semibold text-left transition-all ${
                      decision === opt.id
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 ring-2 ring-teal-500/30'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Override fields */}
            {decision === 'override' && (
              <div className="space-y-3 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                <div>
                  <label className="block text-xs font-semibold text-orange-800 dark:text-orange-300 mb-1">
                    Override Reason Code *
                  </label>
                  <select
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    className="w-full text-xs p-2 rounded border border-orange-300 dark:border-orange-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    {OVERRIDE_REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-orange-800 dark:text-orange-300 mb-1">
                    Agronomist Corrected Diagnosis *
                  </label>
                  <input
                    type="text"
                    value={overrideDiagnosis}
                    onChange={(e) => setOverrideDiagnosis(e.target.value)}
                    placeholder="Enter expert field diagnosis…"
                    className="w-full text-xs p-2 rounded border border-orange-300 dark:border-orange-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                Agronomist Review Notes (Recorded in Audit Trail)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Add agronomic rationale, spray dosage adjustments, or ground verification observations…"
                className="w-full text-xs p-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
              <Button variant="ghost" size="sm" onClick={() => setReviewingMission(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmitReview}
                disabled={submitting || (decision === 'override' && !overrideDiagnosis.trim())}
              >
                {submitting ? 'Applying Digital Seal…' : 'Sign & Release Decision'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisReviewPage;
// Made with Bob
